import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { ReactComponent as DangerIcon } from 'assets/icons/danger.svg';
import EmptyList from 'components/EmptyList/EmptyList';
import { FIELD_KEYS } from 'constants/materialAssessment';
import { useGetGeoRiskAffectedGroupsQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import type IGeoRiskAnalyzeItem from 'store/slices/materialAssessment/interfaces/IGeoRiskAnalyzeItem';
import type { ITableAnalyzeRowProps } from './interfaces/ITableAnalyzeRow';

import styles from './scss/TableAnalyzeRow.module.scss';

const EMPTY_CELL_PLACEHOLDER = '-';

function TableAnalyzeRow({
  item,
  headCells,
  headCellsMiniTableFiltered,
  locationsCountries,
  maId,
  vcmId,
  orderBy,
  refreshAffectedToken,
  level = 0,
}: Readonly<ITableAnalyzeRowProps>) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = item.children.length > 0;
  const isLeaf = !hasChildren;

  const { data: affectedGroupsData, refetch: refetchAffectedGroups } = useGetGeoRiskAffectedGroupsQuery({
    maId,
    id: item.id,
    vcmId,
    offset: 0,
    orderBy,
  }, {
    skip: !isExpanded || !isLeaf,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!isExpanded || !isLeaf || refreshAffectedToken === 0) {
      return;
    }

    refetchAffectedGroups();
  }, [isExpanded, isLeaf, refreshAffectedToken, refetchAffectedGroups]);

  const affectedItems = affectedGroupsData?.data?.items || [];

  const locationCountry = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return country?.name || countryCode;
  };

  const formatCellValue = (value: string | null | undefined) => {
    if (!value?.trim()) {
      return EMPTY_CELL_PLACEHOLDER;
    }

    return value;
  };

  const renderMainTableCell = (cellId: string) => {
    switch (cellId) {
      case FIELD_KEYS.COUNTRY:
      case FIELD_KEYS.COUNTRY_CODE:
        return (
          <div className={styles.countryCell}>
            <DangerIcon className={styles.dangerIcon} />
            <span>{locationCountry(item.countryCode)}</span>
          </div>
        );
      case FIELD_KEYS.CATEGORY_NAME:
      case FIELD_KEYS.CATEGORY_NAMES:
        return formatCellValue(item.categoryNames.join(', '));
      case FIELD_KEYS.SENSITIVITY_NAME:
      case FIELD_KEYS.SENSITIVITY_NAMES:
        return formatCellValue(item.sensitivityNames?.join(', '));
      case FIELD_KEYS.REGIONS:
        return formatCellValue(item.regions?.join(', '));
      default:
        return EMPTY_CELL_PLACEHOLDER;
    }
  };

  const renderMiniTableCell = (analyzeItem: IGeoRiskAnalyzeItem, cellId: string) => {
    switch (cellId) {
      case FIELD_KEYS.ROLE:
        return formatCellValue(analyzeItem.type);
      case FIELD_KEYS.FULL_NAME:
        return formatCellValue(analyzeItem.name);
      case FIELD_KEYS.INDUSTRY:
        return formatCellValue(analyzeItem.naceSection);
      case FIELD_KEYS.STATE:
        return formatCellValue(analyzeItem.addressState);
      default:
        return EMPTY_CELL_PLACEHOLDER;
    }
  };

  return (
    <div className={styles.tableRowGroup}>
      <div className={styles.tableRow}>
        <div className={styles.expandColumn}>
          <button
            type="button"
            className={`${styles.expandButton} ${isExpanded ? styles.expanded : ''}`}
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-label={isExpanded ? t('common.collapse') : t('common.expand')}
          >
            <ChevronIcon />
          </button>
        </div>
        {headCells.map((cell, index) => (
          <div
            key={cell.id}
            className={styles.tableCell}
            style={index === 0 ? { paddingLeft: `${16 + (level * 24)}px` } : undefined}
          >
            {renderMainTableCell(cell.id)}
          </div>
        ))}
      </div>

      {isExpanded && hasChildren ? (
        <div className={styles.childrenRows}>
          {item.children.map((child) => (
            <TableAnalyzeRow
              key={child.id}
              item={child}
              headCells={headCells}
              headCellsMiniTableFiltered={headCellsMiniTableFiltered}
              locationsCountries={locationsCountries}
              maId={maId}
              vcmId={vcmId}
              orderBy={orderBy}
              refreshAffectedToken={refreshAffectedToken}
              level={level + 1}
            />
          ))}
        </div>
      ) : null}

      {isExpanded && isLeaf ? (
        <div className={styles.miniTableContainer}>
          <div className={styles.miniTableHeader}>
            {headCellsMiniTableFiltered.map((cell) => (
              <div key={cell.id} className={styles.miniHeaderCell}>
                {t(`materialityAssessment.${cell.id}`)}
              </div>
            ))}
          </div>

          <div className={styles.miniTableBody}>
            {affectedItems.length > 0 ? affectedItems.map((analyzeItem) => (
              <div key={analyzeItem.id} className={styles.miniTableRow}>
                {headCellsMiniTableFiltered.map((cell) => (
                  <div key={cell.id} className={styles.miniTableCell}>
                    {renderMiniTableCell(analyzeItem, cell.id)}
                  </div>
                ))}
              </div>
            )) : <EmptyList text={t('common.noResultsItems')} />}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default TableAnalyzeRow;
